/**
 * crm-permissions.ts  (v2)
 *
 * RBAC matrix for the CRM plugin.
 * - owner: full read/write on all records in their scope
 * - member: read all, write only their own records + activities
 * - viewer: read only, no writes
 * - cross-room access is always denied
 */

import type { CrmRole, CrmPrincipal, CrmCommandType } from './crm-contracts.js';

// Actions that require at least 'member' role
const MEMBER_WRITE_ACTIONS = new Set<CrmCommandType>([
  'ADD_ACTIVITY',
  'COMPLETE_ACTIVITY',
]);

// Actions that require 'owner' role
const OWNER_ONLY_ACTIONS = new Set<CrmCommandType>([
  'ADD_CONTACT',
  'UPDATE_CONTACT',
  'ARCHIVE_CONTACT',
  'MERGE_CONTACTS',
  'ADD_ORGANIZATION',
  'UPDATE_ORGANIZATION',
  'ARCHIVE_ORGANIZATION',
  'ADD_DEAL',
  'UPDATE_DEAL',
  'MOVE_DEAL_STAGE',
  'ARCHIVE_DEAL',
]);

export class CrmPermissions {
  /**
   * Returns true if the principal is allowed to execute this command.
   */
  canExecute(
    principal: CrmPrincipal,
    commandType: CrmCommandType,
    targetOrganizationId?: string,
  ): boolean {
    const { role, scopedOrganizationIds } = principal;

    // viewers can never write
    if (role === 'viewer') return false;

    // enforce org-scope on scoped members/owners
    if (scopedOrganizationIds && scopedOrganizationIds.length > 0 && targetOrganizationId) {
      if (!scopedOrganizationIds.includes(targetOrganizationId)) return false;
    }

    if (OWNER_ONLY_ACTIONS.has(commandType)) return role === 'owner';
    if (MEMBER_WRITE_ACTIONS.has(commandType)) return role === 'owner' || role === 'member';

    return false;
  }

  /**
   * Returns true if the principal is allowed to read a record.
   */
  canRead(
    principal: CrmPrincipal,
    targetOrganizationId?: string,
  ): boolean {
    const { scopedOrganizationIds } = principal;
    if (scopedOrganizationIds && scopedOrganizationIds.length > 0 && targetOrganizationId) {
      return scopedOrganizationIds.includes(targetOrganizationId);
    }
    return true;
  }

  static ownerPrincipal(actorId: string): CrmPrincipal {
    return { actorId, role: 'owner' };
  }

  static scopedMember(actorId: string, organizationIds: readonly string[]): CrmPrincipal {
    return { actorId, role: 'member', scopedOrganizationIds: organizationIds };
  }
}
